import { z } from 'zod';
import { ClientId } from '../lib/clientId';
import {
  AssortmentExclusionId,
  CargoCompartmentAllocation,
  OrganizationId,
  ScenarioTimeline_CoaAssignmentFragment,
  ScenarioTimeline_HandledBySpotFragment,
  ScenarioTimeline_OffHireFragment,
  ScenarioTimeline_OrderFragment,
  ScenarioTimeline_SingleEventFragment,
  ScheduledOrderFragment,
  ShipId,
  TcContractId,
  VesselArea,
  VesselSpeedTag,
  VesselSpeedType,
} from '../graphql/types';
import type {
  ChannelCostEstimateId,
  LocationId,
  PortCostDefaultId,
} from '../graphql/types';
export type {
  LocationId,
  LocationClusterId as ClusterId,
} from '../graphql/types';

export enum BunkerPriceSource {
  ROB = 'ROB',
  BunkerEx = 'BunkerEx',
}
export declare const BRAND: unique symbol;
export declare type BRAND<T extends string | number | symbol> = {
  [BRAND]: {
    [k in T]: true;
  };
};

export const minimumEventDuration = 2; // milliseconds

export type Constraint = number | null;

export type Order = ScenarioTimeline_OrderFragment;
export type OffHire = ScenarioTimeline_OffHireFragment;
export type HandledBySpot = ScenarioTimeline_HandledBySpotFragment;

export enum EventCategory {
  /**
   * TC events are cargo load/unload events, they carry order information
   */
  TC = 'TC' /** Relates to ScheduledOrder on a TC timeline **/,
  Single = 'SE',
  OffHire = 'OFF',
  Spot = 'S' /** Relates to HandledBySpot on a SPOT timeline **/,
  COA = 'C' /** Relates to HandledByCoa on a COA timeline **/,
  /**
   * PortCall events are port arrival, departure and berthing events
   */
  PortCall = 'PC',
  Leg = 'L',
  Wait = 'W',
  Static = 'ST',
}

type EntryBase = {
  /** @deprecated
   * You should always presume that dbId is outdated in scenario. Never use it unless you're absolutely sure that you have recent data (just fetched) or simply have no alternative.
   * We do not actively update dbId ever.
   */
  dbId: number | null;

  clientId: ClientId;
  updatedAt: number | null;
};

type OrderEntryBase = {
  loadingStart: number;
  loadingEnd: number;
  loadingDuration: number;
  dischargingStart: number;
  dischargingEnd: number;
  dischargingDuration: number;

  loadingConcurrent: boolean;
  dischargingConcurrent: boolean;

  loadingStartOverride: Constraint;
  loadingDurationOverride: number | null;

  loadingSeaDurationOverride: number | null;
  dischargingSeaDurationOverride: number | null;

  dischargingStartOverride: Constraint;
  dischargingDurationOverride: number | null;

  loadingPortCallId: ClientId | null;
  dischargingPortCallId: ClientId | null;
};

export type TCOrderEntry = Pick<ScheduledOrderFragment, 'orderId' | 'shared'> &
  EntryBase &
  OrderEntryBase & {
    category: EventCategory.TC;
    vesselId: ShipId | null;
    shipmentId: ClientId | null;
    /** To avoid re-adding cleaning event if it was removed manually */
    cleaningOmitted: boolean | null;
  };

/** amount is given in m3 */
export type CargoAllocation = Pick<
  CargoCompartmentAllocation,
  'orderId' | 'compartmentId' | 'amount'
> & {
  clientId: ClientId;
  scheduledOrderClientId: ClientId;
};

export type SingleEntry = Pick<
  ScenarioTimeline_SingleEventFragment,
  'type' | 'locationId' | 'locked' | 'meta' | 'shared' | 'vesselId'
> &
  EntryBase & {
    category: EventCategory.Single;
    concurrent: boolean;
    duration: number;
    seaDurationOverride: number | null;
    start: number;
    end: number;
    startOverride: Constraint;
    portCallId: ClientId | null;
    cleaningAssortmentExclusionId?: AssortmentExclusionId | null;
  };

export type OffHireEntry = Pick<
  OffHire,
  | 'locationAId'
  | 'locationBId'
  | 'vesselId'
  | 'locked'
  | 'shared'
  | 'meta'
  | 'type'
  | 'planningOrganisationId'
  | 'userId'
  | 'tcOutRate'
  | 'commissionPct'
  | 'ballastBonus'
  | 'ballastBonusCommissionable'
> &
  EntryBase & {
    category: EventCategory.OffHire;
    start: number;
    end: number;
    duration: number;
  };

export type SpotEntry = Pick<HandledBySpot, 'orderId' | 'shared'> &
  EntryBase &
  OrderEntryBase & {
    category: EventCategory.Spot;
    orderId: Order['id'];
    ownerOrganisationId: OrganizationId | null;
    vesselId: ShipId | null;
    metadata: string | null;
  };

export type CoaEntry = Pick<
  ScenarioTimeline_CoaAssignmentFragment,
  'orderId' | 'coaId' | 'shared'
> &
  EntryBase &
  OrderEntryBase & {
    category: EventCategory.COA;
    vesselId: ShipId | null;
    metadata: string | null;
  };

export type CargoEntry = TCOrderEntry | CoaEntry | SpotEntry;

export const portCallBunkerOverrideSchema = z.object({
  // Matching props
  area: z.nativeEnum(VesselArea),
  type: z.nativeEnum(VesselSpeedType),
  tag: z.nativeEnum(VesselSpeedTag).nullable(),
  // Value props
  bunker_grade_id: z.number(),
});

export const portCallBunkerOverridesSchema = z.array(
  portCallBunkerOverrideSchema,
);

export type PortCallBunkerOverride = z.infer<
  typeof portCallBunkerOverrideSchema
>;

export const portCallBunkerPriceSourceSchema = z.object({
  // Matching props
  area: z.nativeEnum(VesselArea),
  type: z.nativeEnum(VesselSpeedType),
  tag: z.nativeEnum(VesselSpeedTag).nullable(),
  // Value props
  source: z.nativeEnum(BunkerPriceSource),
});

export const portCallBunkerPriceSourcesSchema = z.array(
  portCallBunkerPriceSourceSchema,
);

export type PortCallBunkerPriceSource = z.infer<
  typeof portCallBunkerPriceSourceSchema
>;

export type PortCallEntry = EntryBase & {
  category: EventCategory.PortCall;
  vesselId: ShipId | null;
  locationId: LocationId;
  /** null means "not known", not "none" */
  previousLocationId: LocationId | null;
  plannedArrival: number;
  plannedBerthing: number;
  plannedDeparture: number;
  plannedArrivalOverride: number | null;
  plannedArrivalOverrideSourcedAt: number | null;
  plannedArrivalOverrideSourcedFrom: string | null;
  plannedBerthingOverride: number | null;
  plannedBerthingOverrideSourcedAt: number | null;
  plannedBerthingOverrideSourcedFrom: string | null;
  plannedDepartureOverride: number | null;
  plannedDepartureOverrideSourcedAt: number | null;
  plannedDepartureOverrideSourcedFrom: string | null;
  plannedArrivalDuration: number | null;
  plannedBerthingDuration: number | null;
  plannedDepartureDuration: number | null;
  seaDurationOverride: number | null;
  legAvoidsChannels: boolean;
  totalLoadDurationOverride: number | null | undefined; // Field added without frontend scenario migration, existing data may have the field missing
  totalDischargeDurationOverride: number | null | undefined; // Field added without frontend scenario migration, existing data may have the field missing
  channelCostDefaultId: ChannelCostEstimateId | null;
  oneTimeOverrideChannelCost: number | null;
  oneTimeOverrideCost: number | null;
  portCostDefaultId: PortCostDefaultId | null;
  useHistoricalPortCost: boolean;
  useLatestHistoricalPortCost: boolean;
  useOneTimeOverrideCost: boolean;
  channelDurationOverhead: number | null;
  bunkerOverrides: readonly PortCallBunkerOverride[] | null; // JSON string (BunkerOverrides) added without frontend scenario migration, existing data may have the field missing
  bunkerPriceSource: readonly PortCallBunkerPriceSource[] | null; // JSON string (BunkerPriceSource) added without frontend scenario migration, existing data may have the field missing
  weatherFactor: number | null;
  useIntegrationPortCost: boolean;
};

/** Represents a database entry for a TC In contract */
export type TcInEntryContractDetails = {
  dbId: TcContractId;
  externalId: string;
  deliveryEndDate: number;
  deliveryPortId: LocationId;
  deliveryStartDate: number;
  redeliveryEndDate: number;
  redeliveryPortId: LocationId;
  redeliveryStartDate: number;
  vesselId: ShipId;
};

/**
 * TC Entries are not included in the Entry type as they are not part of the scenario schedule
 * (TC In dates are not managed in the scenario)
 *
 * As TC In entries/events represent periods of time when the vessel is *not* under contract, a TC In Entry is constructed from one or two TC In contracts. previousContracts's redelivery date defines the start boundary of the entry if previous contract is available and nextContract's delivery date defines the end boundary of the entry if a following contract is available.
 */
export type TcInEntry = {
  category: EventCategory.Static;
  clientId: ClientId;
  dbId: null;
  delivery: {
    startDate: number;
    endDate: number;
    locationId: LocationId;
    tcContractExternalId: string;
    tcContractDbId: TcContractId;
  } | null;
  redelivery: {
    startDate: number;
    endDate: number;
    locationId: LocationId;
    tcContractExternalId: string;
    tcContractDbId: TcContractId;
  } | null;
  updatedAt: null;
  vesselId: ShipId;
};

export type Entry =
  | TCOrderEntry
  | SingleEntry
  | OffHireEntry
  | SpotEntry
  | CoaEntry
  | PortCallEntry;

export const isTCOrderEntry = (e: Entry): e is TCOrderEntry =>
  e.category === EventCategory.TC;
export const isSingleEntry = (e: Entry): e is SingleEntry =>
  e.category === EventCategory.Single;
export const isOffHireEntry = (e: Entry): e is OffHireEntry =>
  e.category === EventCategory.OffHire;
export const isSpotEntry = (e: Entry): e is SpotEntry =>
  e.category === EventCategory.Spot;
export const isCoaEntry = (e: Entry): e is CoaEntry =>
  e.category === EventCategory.COA;
export const isPortCallEntry = (e: Entry): e is PortCallEntry =>
  e.category === EventCategory.PortCall;
